import _ from 'lodash';
import { useEffect, useState } from 'react';
import OrderStore from 'ui/run/RunShow/OrderStore/index';
import { RUN_TYPES } from 'common/constants/index';
import classNames from 'classnames';
import RunShowSummary from 'modules/RunSummary/RunShowSummary';
import wrappedFetch from 'common/api/base';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';
import WrapWithButtonBlur from './WrapWithButtonBlur';

interface RunStatisticsProps {
  strategy: {
    id: number;
    title: string;
    margin_amount: number;
    max_drawdown: number;
    tags: Array<string>;
    risk_category: string;
    segment_type: string;
    purchase_type: string;
    purchase_amount: number;
    backtest_run_id: number;
    live_run_id: number;
    description: string;
    backtestJobs: Array<number>;
    strategy_access: {
      id: number;
      is_show_backtest: boolean;
    };
  };
}
const initialState: {
  isLoading: boolean;
  ordersAccessTokens: { [key: string]: string };
} = {
  isLoading: true,
  ordersAccessTokens: {},
};
const RunStatistics = ({ strategy }: RunStatisticsProps) => {
  const shouldShowBacktestInStrategy = ApplicationDomainUtility.shouldShowBacktestInStrategy;
  const { backtestJobs } = strategy;
  const [runStatistics, setRunStatistics] = useState(initialState);
  const oneIndentation = ' ';
  const backtestJobIds = _.map(backtestJobs, 'id').sort();
  const backtestRunId = _.get(backtestJobs, '[0].run_id');

  useEffect(() => {
    if (!backtestJobIds?.length) return;
    const fetchOrderAccessToken = () =>
      wrappedFetch(`/jobs/generate_orders_access_token`, {
        method: 'POST',
        body: JSON.stringify({ jobIds: _.uniq(backtestJobIds) }),
        headers: { 'Content-Type': 'application/json' },
      })
        .then((res) => res.json())
        .then((data: Record<string, string>) =>
          setRunStatistics((prev) => ({ ...prev, ordersAccessTokens: data }))
        )
        .catch(() => setRunStatistics((prev) => ({ ...prev, ordersAccessTokens: {} })))
        .finally(() => setRunStatistics((prev) => ({ ...prev, isLoading: false })));

    fetchOrderAccessToken();
  }, []);

  const strategyClassName = classNames('strategy-order-store', { 'mt-10': runStatistics.isLoading });

  if (!shouldShowBacktestInStrategy) return null;

  return (
    <WrapWithButtonBlur>
      <div className="strategy-order-wrapper">
        <div className={strategyClassName}>
          {!runStatistics.isLoading && (
            <OrderStore
              runIds={[backtestRunId]}
              jobIds={[backtestJobIds]}
              runType={RUN_TYPES.historic}
              ordersAccessTokens={runStatistics.ordersAccessTokens}
              strategyDetails={strategy}
            >
              <RunShowSummary />
            </OrderStore>
          )}
        </div>

        <div className="alert alert-info mb-2">
          <strong>Information : </strong>
          {oneIndentation}
          The past backtesting performance of the strategy is not necessarily indicative of future performance
          of the strategy and trading is subject to market risks. Backtest is based on minute by minute
          data(open / high / low / close), whereas in live deployments are based on second by second data. So
          there will be minor variation. Backtest results does not include
          {oneIndentation}
          <b>brokerage charges/slippages</b>
          {oneIndentation}
          that occur in actual order execution. It is just an indication about the performance of the
          strategy.
        </div>
      </div>
    </WrapWithButtonBlur>
  );
};

export default RunStatistics;
